import React, { useState } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { getUserReports, resolveUserReport } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { Navigate } from 'react-router-dom';
import { isAdministrator } from '../../../client/lib/platformRole';
import {
  Flag,
  CheckCircle,
  Clock,
  MessageSquare,
  MapPin,
  Link2,
  Building2,
  Search,
  ChevronRight,
  X,
  RotateCcw,
} from 'lucide-react';
import { cn } from '../../../lib/utils';

const REPORT_TYPE_LABELS: Record<string, string> = {
  generic_feedback: 'General Feedback',
  report_location: 'Location Report',
  report_post: 'Post Report',
  report_section: 'Section Report',
};

const REPORT_TYPE_COLORS: Record<string, string> = {
  generic_feedback: 'bg-blue-100 text-blue-700',
  report_location: 'bg-orange-100 text-orange-700',
  report_post: 'bg-red-100 text-red-700',
  report_section: 'bg-purple-100 text-purple-700',
};

type StatusFilter = 'all' | 'unread' | 'unresolved' | 'resolved';

function parseContextInfo(raw: string | null) {
  if (!raw) return {} as Record<string, string>;
  const result: Record<string, string> = {};
  raw.split(' | ').forEach((part) => {
    const idx = part.indexOf(': ');
    if (idx === -1) return;
    const key = part.slice(0, idx).toLowerCase();
    const value = part.slice(idx + 2);
    if (key === 'location') result.location = value;
    else if (key === 'org type') result.orgType = value;
    else if (key === 'page') result.page = value;
    else if (key === 'extra') result.extra = value;
  });
  return result;
}

function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function FeedbackPage() {
  const { data: user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<any | null>(null);
  const [resolving, setResolving] = useState(false);

  const resolveReport = useAction(resolveUserReport);

  const { data, isLoading, refetch } = useQuery(getUserReports, {
    status: statusFilter,
    reportType: typeFilter === 'all' ? undefined : typeFilter,
    limit: 100,
  });

  if (!user) return null;
  if (!isAdministrator(user)) return <Navigate to='/' replace />;

  const reports: any[] = data?.reports ?? [];
  const unreadCount: number = data?.unreadCount ?? 0;

  const filtered = reports.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.user?.email?.toLowerCase().includes(s) ||
      r.reason?.toLowerCase().includes(s) ||
      r.details?.toLowerCase().includes(s) ||
      r.contextInfo?.toLowerCase().includes(s)
    );
  });

  const handleResolve = async (reportId: string, markResolved: boolean) => {
    setResolving(true);
    try {
      await resolveReport({ reportId, markResolved });
      await refetch();
      if (selected?.id === reportId) {
        setSelected((prev: any) =>
          prev ? { ...prev, resolvedAt: markResolved ? new Date() : null, isRead: true } : null
        );
      }
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="flex" style={{ height: 'calc(100vh - var(--header-height, 4rem))' }}>
      {/* List panel */}
      <div className="flex flex-col w-full lg:w-[420px] xl:w-[480px] flex-shrink-0 border-r border-border bg-content-bg overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex-shrink-0 space-y-3">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-rose-500" />
            <span className="text-sm font-semibold text-foreground">User Feedback</span>
            {unreadCount > 0 && (
              <span className="text-xs font-medium bg-destructive text-destructive-foreground rounded-full px-2 py-0.5">
                {unreadCount} new
              </span>
            )}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by user, reason, page..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-input-bg border border-input-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex rounded-lg border border-border overflow-hidden text-xs">
              {(['all', 'unread', 'unresolved', 'resolved'] as StatusFilter[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={cn(
                    'px-3 py-1.5 font-medium capitalize transition-colors',
                    statusFilter === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="all">All types</option>
              {Object.entries(REPORT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto divide-y divide-border">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
              <MessageSquare className="w-8 h-8 opacity-30" />
              <p className="text-sm">No reports found</p>
            </div>
          ) : (
            filtered.map((report) => {
              const ctx = parseContextInfo(report.contextInfo);
              const isSelected = selected?.id === report.id;
              const isResolved = !!report.resolvedAt;
              return (
                <button
                  key={report.id}
                  onClick={() => setSelected(report)}
                  className={cn(
                    'w-full text-left px-5 py-3.5 transition-colors flex items-start gap-3',
                    isSelected ? 'bg-primary/10' : 'hover:bg-muted/60',
                    !report.isRead && !isResolved && 'border-l-2 border-primary'
                  )}
                >
                  <div className="flex-shrink-0 mt-1">
                    {isResolved ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : !report.isRead ? (
                      <div className="w-2 h-2 rounded-full bg-primary mt-1" />
                    ) : (
                      <Clock className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className={cn('text-xs font-medium rounded-full px-2 py-0.5', REPORT_TYPE_COLORS[report.reportType] ?? 'bg-muted text-muted-foreground')}>
                        {REPORT_TYPE_LABELS[report.reportType] ?? report.reportType}
                      </span>
                      <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatDate(report.createdAt)}</span>
                    </div>
                    <p className="text-xs font-medium text-foreground truncate">{report.reason || 'No reason selected'}</p>
                    {report.details && <p className="text-xs text-muted-foreground truncate mt-0.5">{report.details}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1">{report.user?.email}</p>
                    {ctx.page && (
                      <p className="text-[10px] text-muted-foreground truncate mt-0.5" title={ctx.page}>
                        {ctx.page.replace(window.location.origin, '')}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                </button>
              );
            })
          )}
        </div>

        {!isLoading && filtered.length > 0 && (
          <div className="px-5 py-2 border-t border-border flex-shrink-0 text-xs text-muted-foreground">
            {filtered.length} report{filtered.length !== 1 ? 's' : ''}
            {data?.total && data.total !== filtered.length ? ` (${data.total} total)` : ''}
          </div>
        )}
      </div>

      {/* Detail panel */}
      <div className="flex-1 hidden lg:flex flex-col overflow-hidden bg-muted/20">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <Flag className="w-10 h-10 opacity-20" />
            <p className="text-sm">Select a report to view details</p>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-border flex-shrink-0 bg-content-bg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={cn('text-xs font-medium rounded-full px-2.5 py-1', REPORT_TYPE_COLORS[selected.reportType] ?? 'bg-muted text-muted-foreground')}>
                  {REPORT_TYPE_LABELS[selected.reportType] ?? selected.reportType}
                </span>
                {selected.resolvedAt ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle className="w-3.5 h-3.5" /> Resolved</span>
                ) : !selected.isRead ? (
                  <span className="flex items-center gap-1 text-xs text-primary font-medium"><div className="w-1.5 h-1.5 rounded-full bg-primary" /> New</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3.5 h-3.5" /> Open</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selected.resolvedAt ? (
                  <button disabled={resolving} onClick={() => handleResolve(selected.id, false)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50">
                    <RotateCcw className="w-3.5 h-3.5" /> Reopen
                  </button>
                ) : (
                  <button disabled={resolving} onClick={() => handleResolve(selected.id, true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
                    <CheckCircle className="w-3.5 h-3.5" /> Mark Resolved
                  </button>
                )}
                <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-muted rounded-lg">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Submitted by</h3>
                <div className="bg-content-bg border border-border rounded-lg px-4 py-3">
                  <p className="text-sm font-medium text-foreground">{selected.user?.username || 'Anonymous'}</p>
                  <p className="text-xs text-muted-foreground">{selected.user?.email}</p>
                  <p className="text-xs text-muted-foreground mt-1">{formatDate(selected.createdAt)}</p>
                </div>
              </section>

              {selected.contextInfo && (() => {
                const ctx = parseContextInfo(selected.contextInfo);
                return (
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Context</h3>
                    <div className="bg-content-bg border border-border rounded-lg px-4 py-3 space-y-2">
                      {ctx.location && (
                        <div className="flex items-center gap-2 text-sm text-foreground">
                          <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span>{ctx.location}</span>
                        </div>
                      )}
                      {ctx.orgType && (
                        <div className="flex items-center gap-2 text-sm text-foreground">
                          <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span>{ctx.orgType}</span>
                        </div>
                      )}
                      {ctx.page && (
                        <div className="flex items-start gap-2 text-sm text-foreground">
                          <Link2 className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <a href={ctx.page} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{ctx.page}</a>
                        </div>
                      )}
                      {ctx.extra && (
                        <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">Note: </span>{ctx.extra}</p>
                      )}
                    </div>
                  </section>
                );
              })()}

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Reason</h3>
                <div className="bg-content-bg border border-border rounded-lg px-4 py-3">
                  <p className="text-sm text-foreground">{selected.reason || <span className="text-muted-foreground italic">No reason selected</span>}</p>
                </div>
              </section>

              {selected.details && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Details</h3>
                  <div className="bg-content-bg border border-border rounded-lg px-4 py-3">
                    <p className="text-sm text-foreground whitespace-pre-wrap">{selected.details}</p>
                  </div>
                </section>
              )}

              {selected.resolvedAt && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Resolution</h3>
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                    <p className="text-sm text-green-700">Resolved on {formatDate(selected.resolvedAt)}</p>
                  </div>
                </section>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}



